import type { PrescriptionFrequency, PrescriptionTiming } from "@/core/clinical/types";
import {
  DEFAULT_PRESCRIPTION_POSOLOGY,
  PRESCRIPTION_FREQUENCY_LABELS,
  PRESCRIPTION_FREQUENCY_OPTIONS,
  PRESCRIPTION_TIMING_LABELS,
  PRESCRIPTION_TIMING_OPTIONS,
} from "@/core/clinical/types";

export interface PosologyFormState {
  frequency: PrescriptionFrequency;
  durationDays: string;
  timing: PrescriptionTiming[];
  instructions: string;
}

export function emptyPosologyFormState(): PosologyFormState {
  return {
    frequency: DEFAULT_PRESCRIPTION_POSOLOGY.frequency,
    durationDays: String(DEFAULT_PRESCRIPTION_POSOLOGY.durationDays),
    timing: [...DEFAULT_PRESCRIPTION_POSOLOGY.timing],
    instructions: "",
  };
}

export function posologyToInput(form: PosologyFormState) {
  const duration = Number(form.durationDays);
  return {
    frequency: form.frequency,
    durationDays: Number.isFinite(duration) && duration > 0 ? duration : null,
    timing: form.timing,
    instructions: form.instructions,
  };
}

interface PrescriptionPosologyFieldsProps {
  form: PosologyFormState;
  idPrefix?: string;
  compact?: boolean;
  onChange: (patch: Partial<PosologyFormState>) => void;
}

export function PrescriptionPosologyFields({
  form,
  idPrefix = "rx-pos",
  compact = false,
  onChange,
}: PrescriptionPosologyFieldsProps) {
  const toggleTiming = (value: PrescriptionTiming) => {
    const next = form.timing.includes(value)
      ? form.timing.filter((t) => t !== value)
      : [...form.timing, value];
    onChange({ timing: next });
  };

  return (
    <div className={compact ? "row g-2" : "row g-2 mt-1"}>
      <div className={compact ? "col-12 col-sm-6 col-md-3" : "col-12 col-sm-6 col-md-4"}>
        <label className="form-label small fw-semibold mb-1" htmlFor={`${idPrefix}-freq`}>
          Fréquence
        </label>
        <select
          id={`${idPrefix}-freq`}
          className="form-select form-select-sm"
          value={form.frequency}
          onChange={(e) => onChange({ frequency: e.target.value as PrescriptionFrequency })}
        >
          {PRESCRIPTION_FREQUENCY_OPTIONS.map((f) => (
            <option key={f} value={f}>
              {PRESCRIPTION_FREQUENCY_LABELS[f]}
            </option>
          ))}
        </select>
      </div>
      <div className="col-12 col-sm-6 col-md-2">
        <label className="form-label small fw-semibold mb-1" htmlFor={`${idPrefix}-dur`}>
          Durée (j)
        </label>
        <input
          id={`${idPrefix}-dur`}
          type="number"
          min={1}
          className="form-control form-control-sm"
          value={form.durationDays}
          onChange={(e) => onChange({ durationDays: e.target.value })}
        />
      </div>
      <div className={compact ? "col-12 col-md-7" : "col-12 col-md-6"}>
        <span className="form-label small fw-semibold mb-1 d-block">Moment</span>
        <div className="d-flex flex-wrap gap-2">
          {PRESCRIPTION_TIMING_OPTIONS.map((t) => (
            <label key={t} className="form-check form-check-inline small mb-0">
              <input
                type="checkbox"
                className="form-check-input"
                checked={form.timing.includes(t)}
                onChange={() => toggleTiming(t)}
              />
              <span className="form-check-label">{PRESCRIPTION_TIMING_LABELS[t]}</span>
            </label>
          ))}
        </div>
      </div>
      {!compact && (
        <div className="col-12">
          <label className="form-label small fw-semibold mb-1" htmlFor={`${idPrefix}-notes`}>
            Précisions posologie
          </label>
          <input
            id={`${idPrefix}-notes`}
            type="text"
            className="form-control form-control-sm"
            placeholder="Ex. 1 comprimé à chaque prise"
            value={form.instructions}
            onChange={(e) => onChange({ instructions: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

interface PrescriptionLinePosologyCellProps {
  line: {
    frequency: PrescriptionFrequency | "";
    durationDays: number | null;
    timing: PrescriptionTiming[];
    instructions: string;
  };
  canEdit: boolean;
  onUpdate: (patch: {
    frequency?: PrescriptionFrequency | "";
    durationDays?: number | null;
    timing?: PrescriptionTiming[];
    instructions?: string;
  }) => void;
}

export function PrescriptionLinePosologyCell({
  line,
  canEdit,
  onUpdate,
}: PrescriptionLinePosologyCellProps) {
  if (!canEdit) {
    return null;
  }

  const toggleTiming = (value: PrescriptionTiming) => {
    const next = line.timing.includes(value)
      ? line.timing.filter((t) => t !== value)
      : [...line.timing, value];
    onUpdate({ timing: next });
  };

  return (
    <div className="d-flex flex-column gap-1" style={{ minWidth: "14rem" }}>
      <select
        className="form-select form-select-sm"
        value={line.frequency || DEFAULT_PRESCRIPTION_POSOLOGY.frequency}
        onChange={(e) => onUpdate({ frequency: e.target.value as PrescriptionFrequency })}
      >
        {PRESCRIPTION_FREQUENCY_OPTIONS.map((f) => (
          <option key={f} value={f}>
            {PRESCRIPTION_FREQUENCY_LABELS[f]}
          </option>
        ))}
      </select>
      <div className="d-flex gap-1">
        <input
          type="number"
          min={1}
          className="form-control form-control-sm"
          style={{ width: "4rem" }}
          value={line.durationDays ?? ""}
          placeholder="j"
          onChange={(e) => {
            const n = Number(e.target.value);
            onUpdate({ durationDays: Number.isFinite(n) && n > 0 ? n : null });
          }}
        />
        <input
          type="text"
          className="form-control form-control-sm flex-grow-1"
          placeholder="Précisions"
          value={line.instructions}
          onChange={(e) => onUpdate({ instructions: e.target.value })}
        />
      </div>
      <div className="d-flex flex-wrap gap-1">
        {PRESCRIPTION_TIMING_OPTIONS.slice(0, 4).map((t) => (
          <button
            key={t}
            type="button"
            className={`btn btn-sm ${line.timing.includes(t) ? "btn-primary" : "btn-outline-secondary"}`}
            style={{ fontSize: "0.7rem", padding: "0.1rem 0.35rem" }}
            onClick={() => toggleTiming(t)}
          >
            {PRESCRIPTION_TIMING_LABELS[t]}
          </button>
        ))}
      </div>
    </div>
  );
}
